import { MessageCircle, Clock, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FormattedCopyMarkdown } from "@/components/form-copy/FormattedCopyMarkdown";

interface ConversationWelcomeProps {
  logoUrl?: string;
  introText?: string;
  totalQuestions: number;
  estimatedMinutes?: number;
  onStart: () => void;
  isStarting?: boolean;
}

/**
 * Welcome screen shown before conversation begins.
 * Sets context and invites the user to start chatting.
 */
export function ConversationWelcome({
  logoUrl,
  introText,
  totalQuestions,
  estimatedMinutes,
  onStart,
  isStarting = false,
}: ConversationWelcomeProps) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        {logoUrl && (
          <div className="flex justify-center">
            <img
              src={logoUrl}
              alt="Form logo"
              className="max-h-16 max-w-[180px] object-contain"
            />
          </div>
        )}

        {/* Welcome card */}
        <div className="rounded-2xl border border-border bg-card p-8 shadow-lg">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
              <MessageCircle className="size-8 text-primary" />
            </div>
          </div>

          {/* Title */}
          <FormattedCopyMarkdown
            content={introText || "Let's have a conversation"}
            variant="intro"
            className="mb-3 text-center"
          />

          {/* Subtitle */}
          <p className="mb-6 text-center text-muted-foreground">
            Answer a few questions through a friendly chat.
            Just type naturally - I'll guide you through it.
          </p>

          {/* Meta info */}
          <div className="mb-8 flex items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Sparkles className="size-4" />
              <span>{totalQuestions} question{totalQuestions !== 1 ? 's' : ''}</span>
            </div>
            {estimatedMinutes && (
              <div className="flex items-center gap-1.5">
                <Clock className="size-4" />
                <span>~{estimatedMinutes} min</span>
              </div>
            )}
          </div>

          {/* Start button */}
          <Button
            onClick={onStart}
            disabled={isStarting}
            size="lg"
            className={cn(
              'w-full gap-2 text-base',
              'transition-all duration-300',
              'hover:scale-[1.02] active:scale-[0.98]'
            )}
          >
            {isStarting ? (
              <>
                <span className="size-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                Starting...
              </>
            ) : (
              <>
                <MessageCircle className="size-5" />
                Start conversation
              </>
            )}
          </Button>
        </div>

        {/* Hint text */}
        <p className="text-center text-xs text-muted-foreground/70">
          Your responses are saved as you go
        </p>
      </div>
    </div>
  );
}
